import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Sparkles, ArrowRight, Crown, Zap, Shield, TrendingUp } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useAuth } from '../contexts/AuthContext';
export function PaymentSuccessPage() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [showContent, setShowContent] = useState(false);
  useEffect(() => {
    document.title = 'Payment Successful - Toxic Detector';
    // Trigger confetti celebration
    triggerConfetti();
    // Show content after brief delay for dramatic effect
    setTimeout(() => setShowContent(true), 300);
  }, []);
  const triggerConfetti = () => {
    const duration = 3000;
    const end = Date.now() + duration;
    const colors = ['#FF0000', '#FFD700', '#FFFFFF'];
    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: {
          x: 0,
          y: 0.6
        },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: {
          x: 1,
          y: 0.6
        },
        colors: colors
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
    // Additional burst from center
    setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: {
          y: 0.6
        },
        colors: colors
      });
    }, 500);
  };
  const benefits = [{
    icon: <Zap size={24} data-id="element-9523" />,
    title: 'Unlimited Scans',
    description: 'Analyze as many conversations as you need'
  }, {
    icon: <Shield size={24} data-id="element-9524" />,
    title: 'Advanced Detection',
    description: 'Access all forensic analysis modes'
  }, {
    icon: <TrendingUp size={24} data-id="element-9525" />,
    title: 'Monthly Reports',
    description: 'Comprehensive insights delivered monthly'
  }];
  return <div className="min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-9526">
      {/* Animated background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,0,0,0.03)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" data-id="element-9527" />

      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,0,0,0.1),transparent_50%)] pointer-events-none" data-id="element-9528" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-4 md:p-8" data-id="element-9529">
        <AnimatePresence data-id="element-9530">
          {showContent && <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="w-full max-w-2xl" data-id="element-9531">
              {/* Success Icon */}
              <motion.div initial={{
            scale: 0,
            rotate: -180
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            delay: 0.2,
            duration: 0.6,
            type: 'spring',
            stiffness: 200
          }} className="flex justify-center mb-8" data-id="element-9532">
                <div className="relative" data-id="element-9533">
                  {/* Pulsing rings */}
                  <motion.div animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className="absolute inset-0 bg-green-500 rounded-full blur-xl" data-id="element-9534" />
                  <motion.div animate={{
                scale: [1, 1.3, 1],
                opacity: [0.3, 0, 0.3]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                delay: 0.5
              }} className="absolute inset-0 bg-[#FFD700] rounded-full blur-xl" data-id="element-9535" />

                  {/* Main icon */}
                  <div className="relative bg-gradient-to-br from-green-500 to-emerald-600 rounded-full p-8 shadow-[0_0_50px_rgba(34,197,94,0.5)]" data-id="element-9536">
                    <CheckCircle size={64} className="text-white" strokeWidth={3} data-id="element-9537" />
                  </div>
                </div>
              </motion.div>

              {/* Main content card */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="bg-[#0a0a0a] rounded-2xl border border-[#FF0000]/30 shadow-[0_0_80px_rgba(255,0,0,0.15)] overflow-hidden" data-id="element-9538">
                {/* Header section */}
                <div className="relative p-8 md:p-12 text-center border-b border-gray-800" data-id="element-9539">
                  {/* Animated sparkles */}
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear'
              }} className="absolute top-8 right-8 text-[#FFD700] opacity-50" data-id="element-9540">
                    <Sparkles size={24} data-id="element-9541" />
                  </motion.div>
                  <motion.div animate={{
                rotate: -360
              }} transition={{
                duration: 15,
                repeat: Infinity,
                ease: 'linear'
              }} className="absolute bottom-8 left-8 text-[#FF0000] opacity-50" data-id="element-9542">
                    <Sparkles size={20} data-id="element-9543" />
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.6
              }} className="inline-flex items-center gap-2 bg-[#FFD700]/10 border border-[#FFD700]/30 px-4 py-2 rounded-full mb-6" data-id="element-9544">
                    <Crown size={16} className="text-[#FFD700]" data-id="element-9545" />
                    <span className="text-[#FFD700] font-mono text-xs font-bold uppercase tracking-wider" data-id="element-9546">
                      Pro Activated
                    </span>
                  </motion.div>

                  <motion.h1 initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.7
              }} className="text-4xl md:text-5xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9547">
                    Payment
                    <br data-id="element-9548" />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500" data-id="element-9549">
                      Successful
                    </span>
                  </motion.h1>

                  <motion.p initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.8
              }} className="text-gray-400 font-mono text-sm md:text-base" data-id="element-9550">
                    Your Pro subscription is now active. Welcome to unlimited
                    forensic analysis.
                  </motion.p>
                </div>

                {/* Benefits section */}
                <div className="p-8 md:p-12" data-id="element-9551">
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.9
              }} className="mb-8" data-id="element-9552">
                    <h2 className="text-white font-bold text-lg uppercase tracking-wide mb-6 flex items-center gap-2" data-id="element-9553">
                      <div className="w-1 h-6 bg-[#FF0000]" data-id="element-9554" />
                      Your Pro Benefits
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-9555">
                      {benefits.map((benefit, index) => <motion.div key={index} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 1 + index * 0.1
                  }} className="bg-[#FF0000]/5 border border-[#FF0000]/20 rounded-xl p-6 hover:bg-[#FF0000]/10 transition-colors group" data-id="element-9556">
                          <div className="text-[#FF0000] mb-3 group-hover:scale-110 transition-transform" data-id="element-9557">
                            {benefit.icon}
                          </div>
                          <h3 className="text-white font-bold text-sm mb-2 uppercase tracking-wide" data-id="element-9558">
                            {benefit.title}
                          </h3>
                          <p className="text-gray-400 text-xs font-mono" data-id="element-9559">
                            {benefit.description}
                          </p>
                        </motion.div>)}
                    </div>
                  </motion.div>

                  {/* CTA Buttons */}
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1.3
              }} className="flex flex-col sm:flex-row gap-4" data-id="element-9560">
                    <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => navigate('/upload')} className="flex-1 group relative overflow-hidden bg-[#FF0000] text-white px-8 py-4 rounded-xl font-bold uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(255,0,0,0.3)] hover:shadow-[0_0_40px_rgba(255,0,0,0.5)]" data-id="element-9561">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" data-id="element-9562" />
                      <span className="relative flex items-center justify-center gap-2" data-id="element-9563">
                        <Sparkles size={20} data-id="element-9564" />
                        Start Scanning
                        <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-9565" />
                      </span>
                    </motion.button>

                    <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => navigate('/dashboard')} className="flex-1 bg-white/10 border border-gray-700 text-white px-8 py-4 rounded-xl font-bold uppercase tracking-wider hover:bg-white/20 transition-colors" data-id="element-9566">
                      Go to Dashboard
                    </motion.button>
                  </motion.div>

                  {/* Footer info */}
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 1.5
              }} className="mt-8 pt-6 border-t border-gray-800 text-center" data-id="element-9567">
                    <p className="text-gray-500 font-mono text-xs" data-id="element-9568">
                      Receipt sent to{' '}
                      <span className="text-[#FFD700]" data-id="element-9569">{user?.email}</span>
                      {' • '}
                      <a href="mailto:support@toxicdetector.com" className="text-[#FF0000] hover:underline" data-id="element-9570">
                        Contact support
                      </a>
                    </p>
                  </motion.div>
                </div>
              </motion.div>

              {/* Floating particles effect */}
              {[...Array(5)].map((_, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 0
          }} animate={{
            opacity: [0, 1, 0],
            y: [-20, -100],
            x: [0, (Math.random() - 0.5) * 100]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            delay: i * 0.5,
            ease: 'easeOut'
          }} className="absolute w-2 h-2 bg-[#FFD700] rounded-full blur-sm" style={{
            left: `${20 + i * 15}%`,
            bottom: '10%'
          }} data-id="element-9571" />)}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}